import { useAuth } from 'react-oidc-context';
import { useCallback, useContext } from 'react';
import { BasketContext } from '~/providers/basketContext';
import { BasketSections, RequirementTypes } from '~/lib/types/enums';
import {
  BasketMeta,
  BasketQuote,
  BasketResponse,
  DateType,
  QuoteAddress,
  QuoteDates,
  QuoteExtra,
  QuoteExtras,
  QuoteExtrasCalculations
} from '~/lib/types/basket-types';
import { BASKET_API } from '~/lib/constants';
import { notifyBasketUpdate } from '~/lib/basket';

import * as Sentry from '@sentry/react';
import { authenticatedFetchWithTenantAnalytics } from '~/lib/fetch';
import { useAnalytics, useTenant } from '@parcel2go/npm-p2g-app';
import { captureException } from '~/lib/metrics';
import {
  filterQuoteDatesWithExtraPrices,
  filterQuoteDatesWithoutExtraPrices,
  getQuotesValidCollectionDates
} from '~/lib/quotes';
import { useShopRequirements } from './use-shop-requirements';
import { ProtectionPrefix } from '~/lib/types/extra-types';

interface IP2GBasketItemOptions {
  /**The name of the basket section */
  name: string;
  /**The basket item id */
  itemId: string;
}

export const useBasketItem = <T = Record<string, string>>({ name, itemId }: IP2GBasketItemOptions) => {
  const { user } = useAuth();
  const { tenant, settings } = useTenant();
  const { session } = useAnalytics();
  const {
    state: { basket, broadcast, events, changes, savingItem, fetching, currentItem },
    dispatch
  } = useContext(BasketContext);

  const item = basket.items[itemId];

  const destinationShopRequirements = useShopRequirements(RequirementTypes.DESTINATION);

  const load = useCallback(async () => {
    if (!fetching) {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: true, saving: false });

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          session,
          `${BASKET_API}/${tenant}/basket/${basket.id}/${currentItem}`
        );

        if (!response.ok) {
          throw new Error(`Failed to load item invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result?.result?.items[currentItem]) {
          throw new Error(`Failed to load item invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({ type: 'UPDATE_ITEM', id: currentItem, item: result.result?.items[currentItem] });
        events.emit('basket_item_fetched', {
          id: currentItem,
          item: result.result?.items[currentItem]
        });
        broadcast.postMessage({
          type: 'basket_item_fetched',
          id: currentItem,
          item: result.result?.items[currentItem]
        });
      } catch (e) {
        captureException(e, 'Failed to load item', { section: 'basket' });
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
    }
  }, [fetching, basket.id, currentItem, events, broadcast, dispatch, user, tenant, settings, session]);

  const changeService = useCallback(
    async (quote: BasketQuote, newExtras?: string[]) => {
      try {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: true });

        const collectionDates = getQuotesValidCollectionDates(quote);
        const collectionDatesWithoutUpsell = collectionDates.filter(filterQuoteDatesWithoutExtraPrices);
        const collectionDatesWithUpsell = collectionDates.filter(filterQuoteDatesWithExtraPrices);
        const collectionDate = collectionDatesWithoutUpsell[0] || collectionDatesWithUpsell[0];

        const extras = basket.items[itemId].sections[BasketSections.EXTRAS]?.payload as QuoteExtras | undefined;

        // If the user has selected new extras, update the extras section to only include those protection extras
        if (newExtras && extras?.extras) {
          const protectionExtras = newExtras.filter((extraKey) => extraKey.startsWith(ProtectionPrefix));

          // Reset Quote Page Logic
          if (extras.initialExtras) {
            extras.initialExtras = quote.availableExtras
              .filter((e) => !e.key.startsWith(ProtectionPrefix) || protectionExtras.includes(e.key))
              .map((e) => ({ key: e.key, metadata: {} }));
          }

          if (protectionExtras.length === 0) {
            extras.extras = extras.extras.filter((extra) => !extra.key.startsWith(ProtectionPrefix));
          } else {
            extras.extras = extras.extras
              .filter((extra) => !extra.key.startsWith(ProtectionPrefix))
              .concat(
                quote.availableExtras
                  .filter((e) => protectionExtras.includes(e.key))
                  .map((e) => ({ key: e.key, metadata: {} }))
              );
          }
        }

        const mutatedExtras: QuoteExtra[] = (extras?.extras || []).filter((extra) =>
          quote.availableExtras.some((a) => a.key === extra.key)
        );

        if (collectionDate && collectionDate.price && collectionDate.extraKey) {
          mutatedExtras.push({ key: collectionDate.extraKey, metadata: {} });
        }

        const initialExtras: QuoteExtra[] = (extras?.initialExtras || []).filter((extra) =>
          quote.availableExtras.some((a) => a.key === extra.key)
        );

        const originShop =
          quote.service.dropOffProviderGroup ===
          (item.sections[BasketSections.QUOTE]?.payload as BasketQuote).service.dropOffProviderGroup
            ? { ...(item.sections[BasketSections.ORIGIN].payload as QuoteAddress).shop }
            : undefined;

        const destinationShop =
          destinationShopRequirements.shopRequirement?.metadata?.ProviderGroup ===
          (item.sections[BasketSections.QUOTE]?.payload as BasketQuote).service.dropOffProviderGroup
            ? { ...(item.sections[BasketSections.DESTINATION].payload as QuoteAddress).shop }
            : undefined;

        const sections = {
          [BasketSections.QUOTE]: { ...item.sections[BasketSections.QUOTE], payload: quote },
          [BasketSections.SERVICE]: {
            ...item.sections[BasketSections.SERVICE],
            payload: { slug: quote.service.slug }
          },
          [BasketSections.EXTRAS]: {
            ...item.sections[BasketSections.EXTRAS],
            payload: {
              ...(item.sections[BasketSections.EXTRAS].payload as BasketMeta),
              extras: mutatedExtras,
              initialExtras: initialExtras
            }
          },
          [BasketSections.EXTRAS_CALCULATIONS]: {
            ...item.sections[BasketSections.EXTRAS_CALCULATIONS],
            payload: {
              ...(item.sections[BasketSections.EXTRAS_CALCULATIONS].payload as QuoteExtrasCalculations),
              ddp: undefined
            }
          },
          [BasketSections.DESTINATION]: {
            ...item.sections[BasketSections.DESTINATION],
            payload: {
              ...(item.sections[BasketSections.DESTINATION].payload as QuoteAddress),
              shop: destinationShop,
              shopId: destinationShop?.id
            } as QuoteAddress
          },
          [BasketSections.ORIGIN]: {
            ...item.sections[BasketSections.ORIGIN],
            payload: {
              ...(item.sections[BasketSections.ORIGIN].payload as QuoteAddress),
              shop: originShop,
              shopId: originShop?.id
            } as QuoteAddress
          },
          [BasketSections.DATES]: {
            ...item.sections[BasketSections.DATES],
            payload: {
              dates:
                collectionDate && collectionDate.collection
                  ? [
                      {
                        type: DateType.Collection,
                        date: collectionDate.collection.date
                      }
                    ]
                  : []
            } as QuoteDates
          }
        };

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          session,
          `${BASKET_API}/${tenant}/basket/${basket.id}/${itemId}`,
          {
            method: 'POST',
            body: JSON.stringify(sections),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to change service invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items[itemId]) {
          throw new Error(
            `Failed to change service invalid api response ${result.error?.code} - ${result.error?.message}`
          );
        }

        notifyBasketUpdate(itemId, dispatch, broadcast, events, result.result);

        Sentry.metrics.increment('service_changed', 1, {
          tags: { section: 'basket' }
        });
      } catch (e) {
        console.error('Failed to change service', e);
        captureException(e, 'Failed to switch service', { section: 'basket' });
        load();
        return false;
      } finally {
        dispatch({ type: 'UPDATE_LOADING', fetching: false, saving: false });
      }
      return true;
    },
    [dispatch, basket.items, basket.id, itemId, item, user, tenant, settings, session, broadcast, events, load]
  );

  const update = useCallback(
    async (item: T) => {
      dispatch({ type: 'SAVING_ITEM', saving: true });

      try {
        const currentItem = basket.items[itemId].sections[name];

        dispatch({ type: 'UPDATE_CHANGES', changes: [...changes] });
        dispatch({ type: 'UPDATE_SECTION_PAYLOAD', id: itemId, name: name, payload: item });

        const response = await authenticatedFetchWithTenantAnalytics(
          user,
          tenant,
          settings,
          session,
          `${BASKET_API}/${tenant}/basket/${basket.id}/${itemId}/${name}`,
          {
            method: 'POST',
            body: JSON.stringify({ ...currentItem, payload: item }),
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Failed to save section invalid http response code ${response.status}`);
        }

        const result = (await response.json()) as BasketResponse;
        if (!result?.success || !result.result?.items[itemId]?.sections[name]) {
          throw new Error(`Failed to save section invalid api response ${result.error?.code}-${result.error?.message}`);
        }

        dispatch({
          type: 'UPDATE_SECTION',
          id: itemId,
          name: name,
          section: result.result?.items[itemId]?.sections[name]
        });
      } catch (e) {
        console.error('Failed to save section', e);
        captureException(e, 'Failed to save section', { section: 'basket' });
        load();
      } finally {
        dispatch({ type: 'SAVING_ITEM', saving: false });
      }
    },
    [dispatch, basket.items, basket.id, itemId, name, changes, user, tenant, settings, session, load]
  );

  return {
    item,
    changeService,
    basketId: basket.id,
    update,
    saving: savingItem,
    load,
    payload: item?.sections[name]?.payload as T
  };
};
